import { Link, useLocation, useNavigate } from "react-router-dom";
import type { MouseEvent as ReactMouseEvent } from "react";
import { useFavorites } from "@/hooks/useFavorites";
import type { Work } from "@/types/work";
import { formatViews } from "@/utils/workUtils";

interface Props {
  work: Work;
}

export default function WorkCard({ work }: Props) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorite = isFavorite(work.id);
  const location = useLocation();
  const navigate = useNavigate();

  const handleFavorite = (event: ReactMouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(work.id);
  };

  const handleShare = (event: ReactMouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(`/detail/${work.id}#share`, { state: { from: location.pathname } });
  };

  return (
    <Link
      to={`/detail/${work.id}`}
      className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden transition-all duration-200 hover:border-primary-300 hover:-translate-y-0.5"
    >
      <div className="relative w-full aspect-[2/3] bg-background-200 overflow-hidden">
        <img
          src={work.poster}
          alt={`${work.title} 海报`}
          title={`${work.title} 韩国伦理影视`}
          className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
        />

        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-foreground-950/70 text-background-50 text-[11px] font-medium">
          {work.type === "movie" ? "伦理电影" : "伦理电视剧"}
        </span>

        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-[11px] font-semibold flex items-center gap-1">
          <i className="ri-star-fill text-[10px]" /> {work.rating.toFixed(1)}
        </span>

        {work.type === "drama" && work.episodes && (
          <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-background-50/90 text-primary-800 text-[11px] font-medium">
            全 {work.episodes} 集
          </span>
        )}

        <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-foreground-950/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-between">
          <span className="text-background-50 text-[11px] flex items-center gap-1">
            <i className="ri-eye-line" /> {formatViews(work.views)}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleShare}
              className="w-7 h-7 flex items-center justify-center rounded-md bg-background-50/20 text-background-50 hover:bg-background-50/35 cursor-pointer"
              aria-label={`分享 ${work.title}`}
            >
              <i className="ri-share-forward-line text-sm" />
            </button>
            <button
              type="button"
              onClick={handleFavorite}
              className={`w-7 h-7 flex items-center justify-center rounded-md cursor-pointer ${
                favorite
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-50/20 text-background-50 hover:bg-background-50/35"
              }`}
              aria-label={favorite ? `取消收藏 ${work.title}` : `收藏 ${work.title}`}
            >
              <i className={`${favorite ? "ri-bookmark-fill" : "ri-bookmark-line"} text-sm`} />
            </button>
          </div>
        </div>
      </div>

      <div className="p-3 flex flex-col gap-1.5">
        <h3 className="font-heading font-semibold text-sm text-foreground-950 line-clamp-1 group-hover:text-primary-700 transition-colors">
          {work.title}
        </h3>
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <span>{work.year}</span>
          <span className="w-px h-3 bg-background-300" />
          <span className="line-clamp-1">{work.genres[0]}</span>
        </div>
        <div className="flex flex-wrap gap-1 mt-0.5">
          {work.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="text-[11px] px-1.5 py-0.5 rounded bg-background-100 text-foreground-600"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}